import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";
import { API_BASE } from "@/lib/api";

interface EmployeeAvatarProps {
  avatarUrl?: string | null;
  avatarColor?: string | null;
  initials: string;
  name?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
}

const sizeClasses: Record<string, string> = {
  xs: "h-6 w-6 text-[10px]",
  sm: "h-8 w-8 text-xs",
  md: "h-11 w-11 text-sm",
  lg: "h-16 w-16 text-lg",
  xl: "h-20 w-20 text-xl",
};

function resolveUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE}${url.startsWith("/") ? "" : "/"}${url}`;
}

export function EmployeeAvatar({
  avatarUrl,
  avatarColor,
  initials,
  name,
  size = "md",
  className,
}: EmployeeAvatarProps) {
  const [imgError, setImgError] = useState(false);
  const sc = sizeClasses[size] ?? sizeClasses.md;
  const resolved = resolveUrl(avatarUrl);

  useEffect(() => {
    setImgError(false);
  }, [avatarUrl]);

  if (resolved && !imgError) {
    return (
      <img
        src={resolved}
        alt={name ?? initials}
        className={cn(sc, "rounded-full object-cover shrink-0", className)}
        onError={() => setImgError(true)}
      />
    );
  }

  const color = avatarColor || "from-slate-400 to-slate-600";
  const isHex = color.startsWith("#");

  return (
    <div
      className={cn(
        sc,
        "rounded-full flex items-center justify-center text-white font-semibold shrink-0",
        !isHex && `bg-gradient-to-br ${color}`,
        className
      )}
      style={isHex ? { backgroundColor: color } : undefined}
    >
      {initials || "?"}
    </div>
  );
}
